import { Component, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@shared/services/user.service';
import { UtilService } from '@shared/utils/util';
import { StoreV2Page } from '../store-v2/store-v2.page';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { InfiniteScrollCustomEvent, IonContent, ModalController, NavController, Platform} from '@ionic/angular';
import { OpenProfileService } from 'src/app/services/open-profile.service';
import { PurchaseService } from 'src/app/services/purchase.service';
import { Preferences } from '@capacitor/preferences';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { VegboostModalComponent } from '@shared/components/vegboost-modal/vegboost-modal.component';
import { VegboostActiveComponent } from '@shared/components/vegboost-active/vegboost-active.component';

@Component({
  selector: 'app-likes-page',
  templateUrl: './likes.page.html',
  styleUrls: ['./likes.page.scss'],
})
export class LikesPage{ 
  @Input() refreshMyData?:  () => void; 
  @Input() vegBoostEnable: boolean = false;
  @ViewChild(IonContent) content!: IonContent;
  private scrollSubscription!: Subscription;

  public me: any;
  public likedBySouls: any[] = [];

  public showScrollButton = false;

  public newLikes: number = 0;
  public currPage: number = 0;
  public totalLikedBy: number = 0;

  public canSeeWhoLikedMe: boolean = false;   
  public isLoading: boolean = false;
  public hasMoreLikes: boolean = true; 

  private backButtonSubscription!: Subscription;

  constructor(private analytics: AnalyticsService,
              public util: UtilService,
              public modalCtrl: ModalController,
              private translate: TranslateService,
              private profileService: OpenProfileService,
              private router: Router,
              public userService: UserService,
              public purchaseServiceProvider: PurchaseService,
              private route: ActivatedRoute,
              private platform: Platform,
              private navCtrl: NavController) {
    this.util.initTranslate();
    }

  /**
   * When the page is entered, we collect the data from the previous page
   * and load the likes
   */
  ionViewWillEnter(){

    if(this.platform.is('android')) {
      this.backButtonSubscription = this.platform.backButton.subscribe(() => {
      });
    }

    this.initialization();
  }

  ngAfterViewInit() {
    // Configure  Scroll Observer
    if (this.content) {
      this.scrollSubscription = this.content.ionScroll.subscribe(async (event: any) => {
        if(event == null){
          return;
        }

        const scrollTop = event.detail.scrollTop;
        const screenHeight = window.innerHeight;      
        const scrollElement = await this.content.getScrollElement();
        const scrollHeight = scrollElement.scrollHeight;
        const threshold = 250;

        // Se o usuário rolou mais que a altura da tela, mostra o botão
        this.showScrollButton = scrollTop > screenHeight;
      
        // Check if we are near the bottom of the page to load more likes
        const isNearBottom = scrollTop + screenHeight >= scrollHeight - threshold;

        if (isNearBottom){
          if (this.isLoading || !this.hasMoreLikes) {
            return;
          }

          this.currPage++;
          try {
            await this.loadLikes(this.currPage);
          } catch (error) {
            console.error('Error loading more data:', error);
            this.currPage--;
          } finally {
          }
        }
      });
    }
  }
  
  /**
   * When the page is left, we reset the data
   */
  ionViewWillLeave(){
    if (this.backButtonSubscription) {
      this.backButtonSubscription.unsubscribe();
    }

    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
    
    this.destruction();
  }

  /**
   * Calls the UserService to load the likes on the likedBySouls array
   * @param page 
   */
  async loadLikes(page: number): Promise<void> {
    if(this.isLoading == true){
      return
    }

    this.isLoading = true;

    let token: any = '0';
    if (localStorage.getItem('TOKEN')) {
      token = localStorage.getItem('TOKEN')
    }

    return new Promise((resolve, reject) => {
      this.userService.findNearby(
        this.me,
        this.me.location.coordinates[0],
        this.me.location.coordinates[1],
        token,
        page,
        true,
        "liked"
      ).subscribe({
        next: (response) => {
          const data = JSON.parse(response);
      
          // Remove deletedLikes message if exists
          if (data[data.length - 1]?.userMessage) {
            data.pop();
          }
  
          // Update data array
          this.likedBySouls = [...this.likedBySouls, ...data];
  
          // Check if we received less than 10 items (assuming 10 per page)
          this.hasMoreLikes = data.length == 10;
          this.isLoading = false;
          resolve();
        },
        error: (err) => {
          console.error('Error loading likes:', err);
          reject(err);
        }
      });
    });
  }

  /**
   * Loads the first page of likes
   */
  private initialization(){
    Preferences.get({ key: 'USER_DATA' }).then((val) => {
      this.me = val.value ? JSON.parse(val.value) : null;
      this.loadInformation();
      this.loadLikes(this.currPage);
    }).catch((error) => {
      console.error("Erro ao obter dados do usuário:", error);
    });
  }

  /**
   * Clean variables in case of getting out of page / reloading
   */
  private destruction(){
    this.likedBySouls = [];
    this.currPage = 0;
    this.hasMoreLikes = true;
    this.isLoading = false;
    this.newLikes = 0;
  }

  /**
   * Handle the refresh whe the page is scrolled top
   * @param event 
   */
  doRefresh(event:any) {
    this.destruction();
    this.initialization();
    event.target.complete();
  }


  /**
   * Button method, to scroll all the way up
   * Can be componentized and applyed in Home and SuperLikes
   */
  refreshPositionInfo(){
    this.currPage = 0;
    this.content.scrollToPoint(0, 0, 300);
  }

  /**
   * Load the information about the likes
   */
  private loadInformation(){
    // Check if the user can see who liked him
    this.canSeeWhoLikedMe = this.purchaseServiceProvider.canSeeWhoLikedMe(this.me);

    // Determine total received likes
    const likedBySouls = this.me?.likedBySouls ?? [];
    this.totalLikedBy = likedBySouls.length;
  
    // Check How many new likes do we have
    const lastSeenLikedByQuant = this.me?.lastSeenLikedByQuant ?? 0;
    
    if(this.canSeeWhoLikedMe){
      this.newLikes = this.totalLikedBy - lastSeenLikedByQuant; 
    }
    else{
      this.newLikes = this.totalLikedBy - lastSeenLikedByQuant - (this.me?.deletedLikes ?? 0); 
    }
  }

  /**
   * Navigate back to the home page and deal with the exiting
   */
  goBack(){
    this.modalCtrl.dismiss();
  }

  /**
   * Navigate to the home page
   */
  async openModalVegBoost() {
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    // Create and show the modal
    const modal = await this.modalCtrl.create({
      component: VegboostModalComponent,
      componentProps: {
        userData: this.me, 
        srcIconBoost: 'assets/imgs/fireboost.svg',
        refreshMyData: this.refreshMyData?.bind(this)
      },
      cssClass: 'custom-class-modal',
      backdropDismiss: true
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();
    document.body.style.overflow = ''; 
    document.body.style.touchAction = '';
  }

    async openActiveVegBoost() {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      // Create and show the modal
      const modal = await this.modalCtrl.create({
        component: VegboostActiveComponent,
        componentProps: {
          userData: this.me, 
          srcIconBoost: 'assets/imgs/fireboost.svg',
          refreshMyData: this.refreshMyData?.bind(this)
        },
        cssClass: 'custom-class-modal',
        backdropDismiss: true
      });
  
      await modal.present();
  
      const { data } = await modal.onDidDismiss();
      document.body.style.overflow = ''; 
      document.body.style.touchAction = '';
    }

  /**
   * Navigate to the profile page
   */
  goToProfilePage(){
    this.goBack();
    this.router.navigateByUrl('/tabs/my-profile-display');
  }

  /**
   * Search for the first available picture of an user
   * @param soul 
   * @returns oid from the first available picture
   */
  getFirstAvailablePicture(soul: any): string {
    for (let i = 0; i <= 8; i++) {
      const pictureSlot = soul[`picture${i}`];
      if (pictureSlot) {
        return pictureSlot.$oid;
      }
    }
    return "";
  }

  /**
   * Open the store page when the button is clicked
   */
  public async openStore() {
      let modal;
      modal = this.modalCtrl.create({ component: StoreV2Page, componentProps: { me: this.me, type: "avocado_club" }, showBackdrop: true, backdropDismiss: true });
      (await modal).present();
      this.analytics.registerEvent(AnalyticsService.avocado);
      this.analytics.registerEvent(AnalyticsService.avcdFromLikedme);
    }

  /**
   * Calls profileService to open the profile
   */
  public async openProfile(soul: any){
    this.profileService.setSouls(this.likedBySouls);
    this.likedBySouls = await this.profileService.openProfile(soul);
  }
}